import { logger } from '@/utils/logging'
import type { VastAd, VastTrackingEvent } from './types'

/**
 * Fetches and parses a VAST (Video Ad Serving Template) XML document.
 * Supports VAST 2.0–4.x and handles wrapper (redirect) chains up to a depth limit.
 */
export async function fetchAndParseVast(vastUri: string, signal?: AbortSignal): Promise<VastAd | null> {
  return resolveVast(vastUri, signal)
}

/** Parses a VAST XML string (used for inline VAST from VMAP). */
export function parseVastXml(xml: string): VastAd | null {
  const parser = new DOMParser()
  const doc = parser.parseFromString(xml, 'text/xml')
  return extractVastAd(doc)
}

const MAX_WRAPPER_DEPTH = 5

async function resolveVast(uri: string, signal: AbortSignal | undefined, depth = 0): Promise<VastAd | null> {
  if (depth >= MAX_WRAPPER_DEPTH) {
    logger().warn('VAST wrapper chain exceeded max depth', { depth: MAX_WRAPPER_DEPTH, uri })
    return null
  }

  try {
    const response = await fetch(uri, { signal })
    if (!response.ok) {
      logger().warn('VAST fetch failed', { status: response.status, uri })
      return null
    }

    const xml = await response.text()
    const parser = new DOMParser()
    const doc = parser.parseFromString(xml, 'text/xml')

    // Check for wrapper — redirect to another VAST document
    const wrapper = doc.querySelector('Wrapper')
    if (wrapper) {
      const adTagUri = wrapper.querySelector('VASTAdTagURI')?.textContent?.trim()
      if (!adTagUri) return null

      const wrappedAd = await resolveVast(adTagUri, signal, depth + 1)
      if (!wrappedAd) return null

      // Merge wrapper tracking into the resolved ad
      mergeWrapperTracking(wrapper, wrappedAd)
      return wrappedAd
    }

    return extractVastAd(doc)
  } catch (error) {
    if (signal?.aborted) return null
    logger().warn('VAST resolution failed', { uri, depth }, error)
    return null
  }
}

function extractVastAd(doc: Document): VastAd | null {
  const inline = doc.querySelector('InLine')
  if (!inline) return null

  const creative = inline.querySelector('Creative')
  if (!creative) return null

  const linear = creative.querySelector('Linear')
  if (!linear) return null

  const mediaFile = selectBestMediaFile(linear)
  if (!mediaFile) return null

  return {
    mediaUrl: mediaFile.url,
    mimeType: mediaFile.mimeType,
    duration: parseDuration(linear.querySelector('Duration')?.textContent),
    skipOffset: parseSkipOffset(linear.getAttribute('skipoffset')),
    clickThrough: linear.querySelector('VideoClicks > ClickThrough')?.textContent?.trim() ?? null,
    clickTracking: collectTextContent(linear.querySelectorAll('VideoClicks > ClickTracking')),
    impressions: collectTextContent(inline.querySelectorAll('Impression')),
    trackingEvents: collectTrackingEvents(linear),
  }
}

interface MediaFileCandidate {
  url: string
  mimeType: string
  width: number
  bitrate: number
}

/** Select the best media file, preferring MP4, highest resolution, then highest bitrate. */
function selectBestMediaFile(linear: Element): MediaFileCandidate | null {
  const mediaFiles = linear.querySelectorAll('MediaFiles > MediaFile')
  const candidates: MediaFileCandidate[] = []

  for (const mf of mediaFiles) {
    const url = mf.textContent?.trim()
    const mimeType = mf.getAttribute('type') ?? ''
    if (!url) continue

    // Only consider video types the browser can play
    if (!mimeType.startsWith('video/')) continue

    candidates.push({
      url,
      mimeType,
      width: parseInt(mf.getAttribute('width') ?? '0', 10),
      bitrate: parseInt(mf.getAttribute('bitrate') ?? '0', 10),
    })
  }

  if (candidates.length === 0) return null

  // Prefer MP4, then by width descending, then by bitrate descending
  candidates.sort((a, b) => {
    const aMp4 = a.mimeType === 'video/mp4' ? 1 : 0
    const bMp4 = b.mimeType === 'video/mp4' ? 1 : 0
    if (aMp4 !== bMp4) return bMp4 - aMp4
    if (a.width !== b.width) return b.width - a.width
    return b.bitrate - a.bitrate
  })

  return candidates[0] ?? null
}

/** Parse VAST duration string "HH:MM:SS" or "HH:MM:SS.mmm" to seconds. */
function parseDuration(text: string | null | undefined): number | null {
  if (!text) return null
  const match = text.trim().match(/^(\d+):(\d+):(\d+)(?:\.(\d+))?$/)
  if (!match) return null

  const hoursStr = match[1]
  const minutesStr = match[2]
  const secondsStr = match[3]
  if (!hoursStr || !minutesStr || !secondsStr) return null

  const hours = parseInt(hoursStr, 10)
  const minutes = parseInt(minutesStr, 10)
  const seconds = parseInt(secondsStr, 10)
  const millis = match[4] ? parseInt(match[4], 10) / Math.pow(10, match[4].length) : 0
  return hours * 3600 + minutes * 60 + seconds + millis
}

/**
 * Parse VAST skipoffset attribute.
 * Supports "HH:MM:SS" (absolute time) and "N%" (percentage — converted using duration).
 */
function parseSkipOffset(skipoffset: string | null): number | null {
  if (!skipoffset) return null

  // Percentage format — not supported without duration context, skip
  if (skipoffset.endsWith('%')) return null

  return parseDuration(skipoffset)
}

function collectTextContent(elements: NodeListOf<Element>): string[] {
  const result: string[] = []
  for (const el of elements) {
    const text = el.textContent?.trim()
    if (text) result.push(text)
  }
  return result
}

const TRACKING_EVENT_NAMES: VastTrackingEvent[] = [
  'creativeView',
  'start',
  'firstQuartile',
  'midpoint',
  'thirdQuartile',
  'complete',
  'skip',
]

function collectTrackingEvents(linear: Element): VastAd['trackingEvents'] {
  const events: VastAd['trackingEvents'] = {}
  const trackingElements = linear.querySelectorAll('TrackingEvents > Tracking')

  for (const el of trackingElements) {
    const eventName = el.getAttribute('event') as VastTrackingEvent | null
    if (!eventName || !TRACKING_EVENT_NAMES.includes(eventName)) continue

    const url = el.textContent?.trim()
    if (!url) continue

    if (!events[eventName]) {
      events[eventName] = []
    }
    events[eventName]!.push(url)
  }

  return events
}

/** Merge tracking URLs from a VAST wrapper into the resolved inline ad. */
function mergeWrapperTracking(wrapper: Element, ad: VastAd): void {
  // Merge impressions
  const wrapperImpressions = collectTextContent(wrapper.querySelectorAll('Impression'))
  ad.impressions.push(...wrapperImpressions)

  // Merge click tracking
  const wrapperClickTracking = collectTextContent(wrapper.querySelectorAll('VideoClicks > ClickTracking'))
  ad.clickTracking.push(...wrapperClickTracking)

  // Merge tracking events
  const creative = wrapper.querySelector('Creative')
  if (creative) {
    const linear = creative.querySelector('Linear')
    if (linear) {
      const wrapperEvents = collectTrackingEvents(linear)
      for (const [event, urls] of Object.entries(wrapperEvents)) {
        const key = event as VastTrackingEvent
        if (!ad.trackingEvents[key]) {
          ad.trackingEvents[key] = []
        }
        ad.trackingEvents[key]!.push(...urls)
      }
    }
  }
}
